import { MapPin, Calendar, Building, CheckCircle, Filter } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SkeletonCard from '@/components/SkeletonCard';
import Breadcrumb from '@/components/Breadcrumb';
import SEO from '@/components/SEO';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects-public');
      if (response.ok) {
        const data = await response.json();
        setAllProjects(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: 'Projelerimiz' }]} />
          <div className="text-center mb-12">
            <div className="h-12 bg-gray-200 rounded w-64 mx-auto mb-6 animate-pulse"></div>
            <div className="w-24 h-1 bg-gray-200 mx-auto mb-8"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const categories = ['Tümü', 'Apartman', 'Villa', 'Konut Sitesi', 'İş Merkezi'];

  const filteredProjects = selectedCategory === 'Tümü' 
    ? allProjects 
    : allProjects.filter(p => p.category === selectedCategory);

  return (
    <>
    <SEO 
      title="Projelerimiz"
      description="MİGA Yapı tamamlanan ve devam eden projeleri. İstanbul Bahçelievler, Bağcılar, Güngören ve çevresinde kentsel dönüşüm ve yeni inşaat projelerimizi inceleyin."
      keywords="miga yapı projeleri, tamamlanan projeler, kentsel dönüşüm projeleri, istanbul inşaat projeleri, apartman projeleri"
    />
    <div data-testid="projects-page" className="min-h-screen pt-24 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Projelerimiz' }]} />
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1
            data-testid="projects-title"
            className="heading-font text-5xl sm:text-6xl font-bold text-[#1e4a7a] mb-6"
          >
            Projelerimiz
          </h1>
          <div className="w-24 h-1 bg-[#dc2626] mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            30 yılı aşkın deneyimimizle İstanbul ve Kocaeli'de başarıyla tamamladığımız projelerimiz
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-[#dc2626] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <Link
              key={project.id || index}
              to={`/projelerimiz/${project.id}`}
              data-testid={`project-card-${index}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 block"
            >
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.images && project.images.length > 0 ? project.images[0] : '/images/placeholder.jpg'}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    project.status === 'Devam Ediyor' 
                      ? 'bg-blue-500 text-white' 
                      : project.status === 'Planlama Aşamasında'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}>
                    {project.status}
                  </div>
                  <div className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                    {project.category}
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-6">
                <h3 className="heading-font text-2xl font-bold text-[#1e4a7a] mb-3">
                  {project.name}
                </h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">{project.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">{project.year}</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                  {project.description}
                </p>
                
                {/* Specs */}
                {project.specs && project.specs.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-3">
                      {project.specs.slice(0, 2).map((spec, idx) => (
                        <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">{spec.label}</div>
                          <div className="text-sm font-semibold text-[#1e4a7a]">{spec.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-12">
          <div className="text-center text-white mb-8">
            <h2 className="heading-font text-4xl font-bold mb-4">Rakamlarla MİGA Yapı</h2>
            <p className="text-lg">30 yılı aşkın sektör tecrübemizle güvenilir hizmet</p>
          </div>
          <div className="grid sm:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="heading-font text-5xl font-bold mb-2">30+</div>
              <div className="text-lg">Yıllık Deneyim</div>
            </div>
            <div>
              <div className="heading-font text-5xl font-bold mb-2">15+</div>
              <div className="text-lg">Tamamlanan Proje</div>
            </div>
            <div>
              <div className="heading-font text-5xl font-bold mb-2">500+</div>
              <div className="text-lg">Mutlu Müşteri</div>
            </div>
            <div>
              <div className="heading-font text-5xl font-bold mb-2">200+</div>
              <div className="text-lg">Toplam Daire</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Projects;